import api from '@/services/api';
import type { AgreementHistoryEvent, HistoryPage, PaymentLinkHistoryChannel } from '../types/agreementTimeline.types';
import { readString } from '../utils/snapshotReaders';
import { readTimelineMeta } from '../utils/timelineMeta';

export const HISTORY_PER_PAGE = 25;

type RawNode = Record<string, unknown>;

const DECIMAL_20_4 = /^\d{1,16}(\.\d{1,4})?$/;

const asString = (value: unknown): string | null => readString(value) ?? null;

const isRecord = (value: unknown): value is RawNode =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const mapBase = (raw: RawNode) => ({
    id: String(raw.id ?? ''),
    occurredAt: String(raw.occurred_at ?? ''),
    actorType: asString(raw.actor_type),
    actorId: asString(raw.actor_id),
    actorName: asString(raw.actor_name),
    crmCaseId: asString(raw.crm_case_id),
});

const DELIVERY_STATES: readonly unknown[] = ['not_requested', 'queued', 'suppressed'];
const SENT_STATES: readonly unknown[] = ['queued', 'suppressed'];

const readLinkChannel = (
    requested: unknown,
    delivery: unknown,
    channel: PaymentLinkHistoryChannel['channel'],
): PaymentLinkHistoryChannel | null | undefined => {
    if (typeof requested !== 'boolean' || !DELIVERY_STATES.includes(delivery)) return undefined;
    if (!requested) return null;
    return SENT_STATES.includes(delivery) ? { channel, suppressed: delivery === 'suppressed' } : undefined;
};

const readLinkChannels = (node: RawNode): PaymentLinkHistoryChannel[] | null => {
    const email = readLinkChannel(node.send_via_email, node.email_delivery, 'email');
    const text = readLinkChannel(node.send_via_sms, node.sms_delivery, 'text');
    if (email === undefined || text === undefined) return null;
    const channels = [email, text].filter((channel): channel is PaymentLinkHistoryChannel => channel !== null);
    return channels.length > 0 ? channels : null;
};

const mapPaymentLinkCreated = (raw: RawNode): AgreementHistoryEvent | null => {
    const node = raw.payment_link;
    if (!isRecord(node) || typeof node.amount !== 'string' || !DECIMAL_20_4.test(node.amount)) return null;
    if (typeof node.pays_off !== 'boolean') return null;
    const channels = readLinkChannels(node);
    if (channels === null) return null;
    return { ...mapBase(raw), kind: 'payment_link_created', paymentLink: { amount: node.amount, paysOff: node.pays_off, channels } };
};

/** An unrecognised entry_type renders as a generic entry rather than vanishing,
 *  so a later slice's backend can ship before its UI. */
export const mapHistoryEvent = (raw: RawNode): AgreementHistoryEvent => {
    const base = mapBase(raw);
    const entryType = String(raw.entry_type ?? '');

    if (entryType === 'note') {
        return { ...base, kind: 'note_added' };
    }

    if (entryType === 'state_change' && isRecord(raw.state_change)) {
        return {
            ...base,
            kind: 'state_change',
            stateChange: {
                fromState: asString(raw.state_change.from_state),
                toState: String(raw.state_change.to_state ?? ''),
                reason: asString(raw.state_change.reason),
            },
        };
    }

    if (entryType === 'top_up_added' && isRecord(raw.top_up)) {
        const { amount, reference } = raw.top_up;
        if (typeof amount === 'string' && DECIMAL_20_4.test(amount) && typeof reference === 'string' && reference !== '') {
            return { ...base, kind: 'top_up_added', topUp: { amount, reference } };
        }
    }

    if (entryType === 'motorplan_payment_received' || entryType === 'motorplan_payment_failed') {
        const amount = isRecord(raw.motorplan_payment) ? raw.motorplan_payment.amount : undefined;
        if (typeof amount === 'string' && DECIMAL_20_4.test(amount)) {
            return { ...base, kind: entryType, amount };
        }
    }

    if (entryType === 'payment_link_created') {
        const mapped = mapPaymentLinkCreated(raw);
        if (mapped) return mapped;
    }

    return { ...base, kind: 'unknown', entryType };
};

/** Order is the server's; entries are never re-sorted on the client. */
export const fetchAgreementHistory = async (
    id: string,
    params: { page: number; perPage: number },
): Promise<HistoryPage> => {
    const raw = (
        await api.get(`/agreements/${id}/history`, { params: { page: params.page, per_page: params.perPage } })
    ).data;
    const meta = isRecord(raw.meta) ? raw.meta : {};

    return {
        entries: ((raw.data ?? []) as RawNode[]).map(mapHistoryEvent),
        meta: readTimelineMeta(meta, params),
    };
};
